import React, { useEffect, useState, useRef } from 'react';
import { Button, Box } from '@mui/material';
import axiosInstance from '../axiosConfig';
import './PrintJobsManagement.css';

const PrintJobsManagement = () => {
  const [printJobs, setPrintJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [modifiedJobs, setModifiedJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState('desc'); // Sortierreihenfolge
  const [orderBy, setOrderBy] = useState('jobId'); // Sortierspalte
  const [statusFilter, setStatusFilter] = useState(''); // Auftragsstatus-Filter
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const messageRef = useRef(null);

  // Druckaufträge abrufen
  useEffect(() => {
    const fetchPrintJobs = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const response = await axiosInstance.get('/admin/print-jobs', {
          headers: {
            Authorization: `${token}`,
          },
        });
        const sortedData = [...response.data].sort((a, b) => {
          if (a.jobId < b.jobId) return 1;
          if (a.jobId > b.jobId) return -1;
          return 0;
        });

        setPrintJobs(sortedData);
        setFilteredJobs(sortedData); // Initial alle Einträge anzeigen
      } catch (error) {
        console.error('Fehler beim Abrufen der Druckaufträge:', error);
      }
    };
    fetchPrintJobs();
  }, []);

  useEffect(() => {
    if ((successMessage || errorMessage) && messageRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [successMessage, errorMessage]);

  // Statusänderung eines Druckauftrags
  const handleStatusChange = (index, status) => {
    const updatedJobs = [...printJobs];
    updatedJobs[index].status = status;
    setPrintJobs(updatedJobs);

    if (!modifiedJobs.find((job) => job._id === updatedJobs[index]._id)) {
      setModifiedJobs([...modifiedJobs, updatedJobs[index]]);
    }
  };

  // Änderungen übernehmen
  const handleSubmitChanges = async () => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axiosInstance.put(
        '/admin/print-jobs/update',
        { modifiedJobs },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        setSuccessMessage('Änderungen erfolgreich übernommen!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Fehler beim Übernehmen der Änderungen:', error);
      setErrorMessage('Fehler beim Übernehmen der Änderungen. Bitte versuchen Sie es erneut.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Suchbegriff-Änderung
  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = printJobs.filter(
      (job) =>
        job.jobId.toLowerCase().includes(value) ||
        job.bookId.toLowerCase().includes(value) ||
        job.bookName.toLowerCase().includes(value) ||
        job.status.toLowerCase().includes(value)
    );

    setFilteredJobs(filtered);
  };

  const handleSort = (column) => {
    const isAsc = order === 'asc' && orderBy === column;
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(column);
    filterAndSortJobs(searchTerm, newOrder, column, statusFilter);
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    filterAndSortJobs(searchTerm, order, orderBy, value);
  };

  const filterAndSortJobs = (search, sortOrder, sortColumn, status) => {
    let jobs = [...printJobs];
  
    // Filter nach Suchbegriff
    if (search) {
      jobs = jobs.filter(
        (job) =>
          job.jobId.toLowerCase().includes(search) ||
          job.bookId.toLowerCase().includes(search) ||
          job.bookName.toLowerCase().includes(search) ||
          job.school?.name?.toLowerCase().includes(search) // Schulname in Suche aufnehmen
      );
    }
  
    // Filter nach Auftragsstatus
    if (status) {
      jobs = jobs.filter((job) => job.status === status);
    }
  
    // Sortieren
    jobs.sort((a, b) => {
      let valueA = a[sortColumn];
      let valueB = b[sortColumn];
  
      // Falls Spalte `createdAt`, konvertiere in Date-Objekte
      if (sortColumn === 'createdAt') {
        valueA = new Date(a.createdAt);
        valueB = new Date(b.createdAt);
      }
  
      // Falls `school.name`, korrekt aus verschachteltem Objekt holen
      if (sortColumn === 'school.name') {
        valueA = a.school?.name?.toLowerCase() || '';
        valueB = b.school?.name?.toLowerCase() || '';
      }
  
      if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  
    setFilteredJobs(jobs);
  };  

  // Safe format helpers to avoid toFixed on undefined/null/strings
  const toNum = (v) => (typeof v === 'number' ? v : v == null ? NaN : Number(v));
  const isFiniteNumber = (v) => Number.isFinite(toNum(v));
  const formatPrice = (v) => (isFiniteNumber(v) ? toNum(v).toFixed(2) : '—');
  const formatPercent = (v) => (isFiniteNumber(v) ? (toNum(v) * 100).toFixed(0) : '—');

  return (
    <Box sx={{ ml: '1rem', mr: '1rem'}}>
      {successMessage && (
        <div ref={messageRef} style={{ backgroundColor: 'rgba(0, 0, 255, 0.1)', padding: '10px', borderRadius: '5px', margin: '10px 0' }}>
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div ref={messageRef} style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', padding: '10px', borderRadius: '5px', margin: '10px 0' }}>
          {errorMessage}
        </div>
      )}

      <div style={{ marginBottom: '10px', marginRight: '1rem', marginTop: '1rem' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Suchen nach Auftragsnummer, Book-ID, Book-Name oder Auftragsstatus"
          style={{ padding: '8px', width: '100%', borderRadius: '5px', border: '1px solid #ccc' }}
        />
      </div>

      <table>
        <thead>
          <tr>
            <th onClick={() => handleSort('jobId')} style={{ cursor: 'pointer' }}>
              Auftrags-Nr. {orderBy === 'jobId' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
              Datum {orderBy === 'createdAt' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th>
              <select value={statusFilter} onChange={handleStatusFilterChange}>
                <option value="">Alle</option>
                <option value="ordered">ordered</option>
                <option value="paid">paid</option>
                <option value="payment pending">payment pending</option>
                <option value="printed">printed</option>
                <option value="shipped">shipped</option>
                <option value="delivered">delivered</option>
                <option value="cancelled">cancelled</option>
              </select>
            </th>
            <th onClick={() => handleSort('school.name')} style={{ cursor: 'pointer' }}>
              Schule {orderBy === 'school.name' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('bookName')} style={{ cursor: 'pointer' }}>
              Buch {orderBy === 'bookName' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('copies')} style={{ cursor: 'pointer' }}>
              Ex. {orderBy === 'copies' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('pagesCount')} style={{ cursor: 'pointer' }}>
              Seiten {orderBy === 'pagesCount' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('pricePerCopy')} style={{ cursor: 'pointer' }}>
              Preis/Ex. {orderBy === 'pricePerCopy' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('totalPrice')} style={{ cursor: 'pointer' }}>
              Betrag {orderBy === 'totalPrice' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('discount')} style={{ cursor: 'pointer' }}>
              Rabatt {orderBy === 'discount' && (order === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('finalPrice')} style={{ cursor: 'pointer' }}>
              Gesamt {orderBy === 'finalPrice' && (order === 'asc' ? '↑' : '↓')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job, index) => (
              <tr key={job._id}>
                <td>{job.jobId}</td>
                <td>{new Date(job.createdAt).toLocaleDateString('de-DE')}</td> {/* Datum formatieren */}
                <td>
                  <select value={job.status} onChange={(e) => handleStatusChange(index, e.target.value)}>
                    <option value="ordered">ordered</option>
                    <option value="paid">paid</option>
                    <option value="payment pending">payment pending</option>
                    <option value="printed">printed</option>
                    <option value="shipped">shipped</option>
                    <option value="delivered">delivered</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </td>
                <td>{job.schoolName}</td> {/* Falls keine Schulinfo, 'Keine Angabe' */}
                <td>{job.bookName}</td>
                <td>{job.copies}</td>
                <td>{job.pagesCount}</td>
                <td>{formatPrice(job.copyPrice)} €</td>
                <td>{formatPrice(job.totalPrice)} €</td>
                <td>{formatPercent(job.discountAmount)} %</td>
                <td>{formatPrice(job.priceFinal)} €</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="11" style={{ textAlign: 'center' }}>Keine Einträge gefunden.</td>
            </tr>
          )}
        </tbody>
      </table>
      <Button variant="contained" size="small" color="primary" onClick={handleSubmitChanges} sx={{ mb: 2, bgcolor: 'primary.light', '&:hover': { color: '#fff' } }} type="button">
        Änderungen übernehmen
      </Button>
    </Box>
  );
};

export default PrintJobsManagement;